import { API_PREFIX, AuditPage, BatteryHistory, CurrentUser, EnergyHistory, HealthResponse, IngestStatus, InstanceSettingsResponse, LatestReading, LoginResponse, OutagePage, SessionList, SiteInfo, Tariff, TariffPage, } from "@spf/contracts";
import { z } from "zod";
import { createTransport } from "./transport.js";
import { streamTelemetry } from "./stream.js";
export { ApiError } from "./errors.js";
function energyPath(query, path = "energy") {
    const params = new URLSearchParams({ resolution: query.resolution });
    if (query.last !== undefined)
        params.set("last", String(query.last));
    if (query.since !== undefined)
        params.set("since", query.since);
    return `${API_PREFIX}/${path}?${params.toString()}`;
}
function pagedPath(path, query) {
    const params = new URLSearchParams();
    if (query.limit !== undefined)
        params.set("limit", String(query.limit));
    if (query.before !== undefined)
        params.set("before", query.before);
    const search = params.toString();
    return `${API_PREFIX}/${path}${search.length > 0 ? `?${search}` : ""}`;
}
/** The lists paged by cursor, newest first, as section 10 requires of anything that grows. */
function pagedLists(request) {
    return {
        audit: (query = {}) => request(pagedPath("audit", query), AuditPage),
        tariffs: (query = {}) => request(pagedPath("tariffs", query), TariffPage),
        outages: (query = {}) => request(pagedPath("outages", query), OutagePage),
    };
}
/**
 * One method per endpoint, each returning parsed, contract-checked data. The
 * web app and any later native client use this same object, so behaviour that
 * belongs to "talking to this API" -- renewing a token, giving up on a session
 * -- lives here rather than in a UI (docs/REQUIREMENTS.md section 3).
 */
export function createClient(options) {
    const transport = createTransport(options);
    const { request, post, tokens, store, signOut } = transport;
    return {
        health: () => request("/health", HealthResponse, { authenticated: false }),
        /** Stores the pair on success, so no caller can forget the refresh token. */
        login: async (email, password) => {
            const pair = await request(`${API_PREFIX}/auth/login`, LoginResponse, post({ email, password }));
            store({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
            return pair;
        },
        /**
         * Best effort on the server, unconditional locally. Someone signing out on
         * a shared machine has to end up signed out whether or not the network
         * cooperates, and the token is gone from this device either way.
         */
        logout: async () => {
            const current = tokens();
            try {
                if (current !== null) {
                    const body = post({ refreshToken: current.refreshToken });
                    await request(`${API_PREFIX}/auth/logout`, z.null(), body);
                }
            }
            catch {
                // Reported to nobody: the local sign-out below is what was asked for.
            }
            finally {
                signOut();
            }
        },
        me: () => request(`${API_PREFIX}/auth/me`, CurrentUser),
        site: () => request(`${API_PREFIX}/site`, SiteInfo),
        settings: () => request(`${API_PREFIX}/settings`, InstanceSettingsResponse),
        /**
         * A full replacement, matching the endpoint. The response is the stored
         * snapshot rather than an echo of the body, so a caller renders what the
         * database holds -- the two differ wherever a coercion applies.
         */
        saveSettings: (values) => request(`${API_PREFIX}/settings`, InstanceSettingsResponse, transport.put(values)),
        sessions: () => request(`${API_PREFIX}/auth/sessions`, SessionList),
        revokeSession: (id) => request(`${API_PREFIX}/auth/sessions/${encodeURIComponent(id)}`, z.null(), {
            method: "DELETE",
        }),
        ...pagedLists(request),
        latestReading: () => request(`${API_PREFIX}/readings/latest`, LatestReading),
        ingestStatus: () => request(`${API_PREFIX}/ingest/status`, IngestStatus),
        energy: (query) => request(energyPath(query), EnergyHistory),
        battery: (query) => request(energyPath(query, "energy/battery"), BatteryHistory),
        addTariff: (tariff) => request(`${API_PREFIX}/tariffs`, Tariff, { method: "POST", body: JSON.stringify(tariff) }),
        deleteTariff: (id) => request(`${API_PREFIX}/tariffs/${encodeURIComponent(id)}`, z.null(), { method: "DELETE" }),
        /**
         * The same reading as `latestReading`, pushed. Held open and reconnected
         * until the handle is closed; see `streamTelemetry` for why that is the
         * whole point rather than a refinement.
         */
        streamTelemetry: (handlers) => streamTelemetry({ transport, path: `${API_PREFIX}/telemetry/stream` }, handlers),
    };
}
//# sourceMappingURL=index.js.map